/**
 * @fileoverview Searchable icon selector for shared admin forms and builders.
 * @module ui-admin/components/IconPicker
 *
 * Responsibilities:
 * - Search and select fire-service, game, and Lucide icons.
 * - Provide list and grid selection modes with keyboard navigation.
 * Dependencies: React, lucide-react, react-icons/gi, fire-service icon registry.
 * Architecture: Reusable presentation component; selected icon is controlled by props.
 * Notes: Rendered result counts are capped to keep the large icon catalog responsive.
 */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { icons } from 'lucide-react';
import { X, LayoutGrid, Search } from 'lucide-react';
import * as GiIcons from 'react-icons/gi';
import { FIRE_SERVICE_ICONS, type FireServiceIconEntry } from './icons/fire-service';

// ── Build icon list once at module level ──────────────────────────
type IconCategory = 'fire-service' | 'game' | 'lucide';
type IconEntry = {
  pascal: string;
  kebab: string;
  label: string;
  searchWords: string[]; // pre-computed lowercase words for search
  category: IconCategory;
  Component: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
};

function pascalToKebab(s: string): string {
  return s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function pascalToLabel(s: string): string {
  return s.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}

// Split a pascal-case name into lowercase words for search
function pascalToWords(s: string): string[] {
  return s
    .replace(/^(Gi|Fs|Lu)/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

// Fire service icons (prefixed with fs-) — always shown first
const FIRE_ICON_LIST: IconEntry[] = FIRE_SERVICE_ICONS.map((entry: FireServiceIconEntry) => ({
  pascal: entry.pascal,
  kebab: entry.kebab,
  label: entry.label,
  searchWords: [...pascalToWords(entry.pascal), ...entry.label.toLowerCase().split(/\s+/)],
  category: 'fire-service' as const,
  Component: entry.Component as IconEntry['Component'],
}));

// Game Icons (react-icons/gi) — ~4,000 thematic icons
const GAME_ICON_LIST: IconEntry[] = Object.entries(GiIcons)
  .filter(([key]) => key.startsWith('Gi'))
  .map(([pascal, Component]) => {
    const label = pascalToLabel(pascal.replace(/^Gi/, ''));
    return {
      pascal,
      kebab: 'gi-' + pascalToKebab(pascal.replace(/^Gi/, '')),
      label,
      searchWords: pascalToWords(pascal),
      category: 'game' as const,
      Component: Component as IconEntry['Component'],
    };
  });

// Lucide icons
const LUCIDE_ICON_LIST: IconEntry[] = Object.entries(icons).map(([pascal, Component]) => {
  const label = pascalToLabel(pascal);
  return {
    pascal,
    kebab: pascalToKebab(pascal),
    label,
    searchWords: pascalToWords(pascal),
    category: 'lucide' as const,
    Component: Component as IconEntry['Component'],
  };
});

// Combined: fire service → game icons → lucide
const ICON_LIST: IconEntry[] = [...FIRE_ICON_LIST, ...GAME_ICON_LIST, ...LUCIDE_ICON_LIST];

// Fast lookup map for getIconComponent
const ICON_MAP = new Map<string, IconEntry>();
ICON_LIST.forEach((e) => {
  ICON_MAP.set(e.pascal, e);
  ICON_MAP.set(e.kebab, e);
});

/** Converts a kebab-case icon identifier to PascalCase. */
export function kebabToPascal(s: string): string {
  return s.replace(/(^|-)([a-z])/g, (_, _p, c) => c.toUpperCase());
}

/** Resolves a bundled icon component by kebab-case or PascalCase identifier. */
export function getIconComponent(kebabName: string | undefined): IconEntry['Component'] | null {
  if (!kebabName) return null;
  // Direct lookup by kebab name first (handles fs- prefixed names)
  const byKebab = ICON_MAP.get(kebabName);
  if (byKebab) return byKebab.Component;
  // Fallback to pascal conversion for Lucide names
  const pascal = kebabToPascal(kebabName);
  const byPascal = ICON_MAP.get(pascal);
  return byPascal?.Component ?? null;
}

// ── IconPicker Component ──────────────────────────────────────────

interface IconPickerProps {
  value: string | undefined;
  onChange: (name: string | undefined) => void;
  placeholder?: string;
  className?: string;
}

const MAX_LIST_RESULTS = 50;
const MAX_GRID_RESULTS = 120;

/**
 * @component IconPicker
 * Purpose: Let users search and choose an icon from the supported icon catalogs.
 * Props: Controlled icon name, change callback, optional placeholder and class name.
 * Behavior: Supports filtered list/grid views, keyboard navigation, selection, and clearing.
 * Data Source: local (bundled icon registries and component state).
 * Constraints: Selection is reported as a kebab-case icon name; no persistence is performed.
 * Performance: Limits rendered matches to 50 in list mode and 120 in grid mode.
 */
export function IconPicker({ value, onChange, placeholder = 'Search icons...', className }: IconPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownMode, setDropdownMode] = useState<'list' | 'grid'>('list');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const gridSearchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // ── Resolve selected icon ────────────────────────────────────
  const selectedEntry = useMemo(() => {
    if (!value) return null;
    const pascal = kebabToPascal(value);
    return ICON_LIST.find((e) => e.pascal === pascal) ?? null;
  }, [value]);

  // ── Filter icons (improved: word-splitting + partial match) ────
  const filteredIcons = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const limit = dropdownMode === 'grid' ? MAX_GRID_RESULTS : MAX_LIST_RESULTS;
    if (!term) return ICON_LIST.slice(0, limit);

    const queryWords = term.split(/\s+/).filter(Boolean);

    const scored: { entry: IconEntry; score: number }[] = [];
    for (const entry of ICON_LIST) {
      let score = 0;
      const kebabLower = entry.kebab;
      const labelLower = entry.label.toLowerCase();

      if (kebabLower.includes(term) || labelLower.includes(term)) {
        score += 100;
      }

      let allWordsMatch = true;
      for (const qw of queryWords) {
        const wordMatch = entry.searchWords.some(sw => sw.startsWith(qw) || sw.includes(qw));
        if (wordMatch) {
          score += 10;
        } else {
          allWordsMatch = false;
        }
      }

      if (score > 0 && allWordsMatch) {
        scored.push({ entry, score });
      }
    }

    scored.sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label));
    return scored.slice(0, limit).map(s => s.entry);
  }, [searchTerm, dropdownMode]);

  // ── Click outside ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Reset highlight when filtered list changes ───────────────
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredIcons.length, searchTerm]);

  // ── Scroll highlighted item into view (list mode) ────────────
  useEffect(() => {
    if (dropdownMode !== 'list' || !listRef.current) return;
    const item = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, dropdownMode]);

  // ── Select an icon ───────────────────────────────────────────
  const selectIcon = useCallback((entry: IconEntry) => {
    onChange(entry.kebab);
    setSearchTerm('');
    setIsOpen(false);
    setIsFocused(false);
    inputRef.current?.blur();
  }, [onChange]);

  // ── Clear selection ──────────────────────────────────────────
  const clearSelection = useCallback(() => {
    onChange(undefined);
    setSearchTerm('');
    setIsOpen(false);
  }, [onChange]);

  // ── Keyboard handling ────────────────────────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        setDropdownMode('list');
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, filteredIcons.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredIcons[highlightedIndex]) {
          selectIcon(filteredIcons[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  }, [isOpen, filteredIcons, highlightedIndex, selectIcon]);

  // ── Input focus/change ───────────────────────────────────────
  const handleInputFocus = () => {
    setIsFocused(true);
    if (selectedEntry) {
      setSearchTerm('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (!isOpen && val.length > 0) {
      setIsOpen(true);
      setDropdownMode('list');
    }
    if (val.length === 0 && dropdownMode === 'list') {
      setIsOpen(false);
    }
  };

  // ── Toggle grid mode ────────────────────────────────────────
  const toggleGrid = () => {
    if (isOpen && dropdownMode === 'grid') {
      setIsOpen(false);
    } else {
      setDropdownMode('grid');
      setIsOpen(true);
      setTimeout(() => gridSearchRef.current?.focus(), 50);
    }
  };

  // ── Grid search change ──────────────────────────────────────
  const handleGridSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // ── Display value in input ──────────────────────────────────
  const inputValue = isFocused ? searchTerm : (selectedEntry?.label ?? '');

  return (
    <div ref={wrapperRef} className={`relative ${className ?? ''}`}>
      {/* ── Input row ──────────────────────────────────────── */}
      <div className="flex items-center border rounded-md focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500 bg-white">
        {/* Selected icon preview (inline left) */}
        {selectedEntry && !isFocused && (
          <span className="pl-2.5 shrink-0 text-gray-600">
            <selectedEntry.Component size={16} />
          </span>
        )}

        <input
          ref={inputRef}
          type="text"
          className="flex-1 px-2.5 py-1.5 text-sm bg-transparent outline-hidden border-none focus:ring-0"
          placeholder={selectedEntry && !isFocused ? '' : placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={() => {
            // Delay to allow click events on dropdown items
            setTimeout(() => {
              if (!wrapperRef.current?.contains(document.activeElement)) {
                setIsFocused(false);
              }
            }, 150);
          }}
          onKeyDown={handleKeyDown}
        />

        {/* Clear button */}
        {value && (
          <button
            type="button"
            onClick={clearSelection}
            className="px-1.5 text-gray-400 hover:text-gray-600 shrink-0"
            tabIndex={-1}
          >
            <X size={14} />
          </button>
        )}

        {/* Grid toggle button */}
        <button
          type="button"
          onClick={toggleGrid}
          className={`px-2 py-1.5 border-l text-gray-400 hover:text-orange-600 hover:bg-orange-50 shrink-0 rounded-r-md transition-colors ${
            isOpen && dropdownMode === 'grid' ? 'text-orange-600 bg-orange-50' : ''
          }`}
          tabIndex={-1}
          title="Browse all icons"
        >
          <LayoutGrid size={14} />
        </button>
      </div>

      {/* ── Dropdown ───────────────────────────────────────── */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg overflow-hidden"
          style={{ minWidth: dropdownMode === 'grid' ? 320 : undefined }}>

          {/* ── List mode (type-ahead) ─────────────────────── */}
          {dropdownMode === 'list' && (
            <div ref={listRef} className="max-h-[220px] overflow-y-auto">
              {filteredIcons.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-gray-400">
                  No icons match &apos;{searchTerm}&apos;
                </div>
              ) : (
                filteredIcons.map((entry, idx) => (
                  <button
                    key={entry.pascal}
                    type="button"
                    className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors ${
                      idx === highlightedIndex
                        ? 'bg-orange-50 text-orange-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    } ${entry.kebab === value ? 'font-medium' : ''}`}
                    onClick={() => selectIcon(entry)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                  >
                    <entry.Component size={16} className="shrink-0" />
                    <span className="truncate">{entry.label}</span>
                    {entry.category === 'fire-service' && entry.kebab !== value && (
                      <span className="ml-auto text-[9px] font-medium text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded-sm">FS</span>
                    )}
                    {entry.category === 'game' && entry.kebab !== value && (
                      <span className="ml-auto text-[9px] font-medium text-purple-500 bg-purple-50 px-1.5 py-0.5 rounded-sm">GI</span>
                    )}
                    {entry.kebab === value && (
                      <span className="ml-auto text-orange-500 text-[10px] font-semibold">Selected</span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {/* ── Grid mode (browse) ─────────────────────────── */}
          {dropdownMode === 'grid' && (
            <>
              {/* Grid search bar */}
              <div className="px-2 pt-2 pb-1.5 border-b">
                <div className="flex items-center gap-1.5 border rounded-md px-2 py-1 bg-gray-50 focus-within:ring-1 focus-within:ring-orange-400 focus-within:border-orange-400">
                  <Search size={13} className="text-gray-400 shrink-0" />
                  <input
                    ref={gridSearchRef}
                    type="text"
                    className="flex-1 text-xs bg-transparent outline-hidden border-none focus:ring-0 placeholder-gray-400"
                    placeholder="Filter icons..."
                    value={searchTerm}
                    onChange={handleGridSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setIsOpen(false);
                      }
                    }}
                  />
                  {searchTerm && (
                    <button type="button" onClick={() => setSearchTerm('')}
                      className="text-gray-400 hover:text-gray-600" tabIndex={-1}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>

              {/* Icon grid */}
              <div className="max-h-[320px] overflow-y-auto p-2">
                {filteredIcons.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-gray-400">
                    No icons match &apos;{searchTerm}&apos;
                  </div>
                ) : (
                  <>
                    {/* Fire Service category */}
                    {filteredIcons.some((e) => e.category === 'fire-service') && (
                      <>
                        <div className="flex items-center gap-1.5 px-1 pt-1 pb-1.5">
                          <span className="text-[10px] font-semibold text-orange-600 uppercase tracking-wider">Fire Service</span>
                          <div className="flex-1 border-t border-orange-200" />
                        </div>
                        <div className="grid grid-cols-6 gap-0.5 mb-2">
                          {filteredIcons.filter((e) => e.category === 'fire-service').map((entry) => (
                            <button
                              key={entry.pascal}
                              type="button"
                              title={entry.label}
                              className={`flex items-center justify-center p-2 rounded-md transition-colors ${
                                entry.kebab === value
                                  ? 'bg-orange-100 ring-2 ring-orange-400 text-orange-700'
                                  : 'text-orange-600 hover:bg-orange-50 hover:text-orange-800'
                              }`}
                              onClick={() => selectIcon(entry)}
                            >
                              <entry.Component size={20} />
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    {/* Game Icons category */}
                    {filteredIcons.some((e) => e.category === 'game') && (
                      <>
                        <div className="flex items-center gap-1.5 px-1 pt-1 pb-1.5">
                          <span className="text-[10px] font-semibold text-purple-600 uppercase tracking-wider">Game Icons</span>
                          <div className="flex-1 border-t border-purple-200" />
                        </div>
                        <div className="grid grid-cols-6 gap-0.5 mb-2">
                          {filteredIcons.filter((e) => e.category === 'game').map((entry) => (
                            <button
                              key={entry.pascal}
                              type="button"
                              title={entry.label}
                              className={`flex items-center justify-center p-2 rounded-md transition-colors ${
                                entry.kebab === value
                                  ? 'bg-purple-100 ring-2 ring-purple-400 text-purple-700'
                                  : 'text-purple-600 hover:bg-purple-50 hover:text-purple-800'
                              }`}
                              onClick={() => selectIcon(entry)}
                            >
                              <entry.Component size={20} />
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    {/* General (Lucide) icons */}
                    {filteredIcons.some((e) => e.category === 'lucide') && (
                      <>
                        {filteredIcons.some((e) => e.category !== 'lucide') && (
                          <div className="flex items-center gap-1.5 px-1 pt-1 pb-1.5">
                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">General</span>
                            <div className="flex-1 border-t border-gray-200" />
                          </div>
                        )}
                        <div className="grid grid-cols-6 gap-0.5">
                          {filteredIcons.filter((e) => e.category === 'lucide').map((entry) => (
                            <button
                              key={entry.pascal}
                              type="button"
                              title={entry.label}
                              className={`flex items-center justify-center p-2 rounded-md transition-colors ${
                                entry.kebab === value
                                  ? 'bg-orange-100 ring-2 ring-orange-400 text-orange-700'
                                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                              }`}
                              onClick={() => selectIcon(entry)}
                            >
                              <entry.Component size={20} />
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
                {filteredIcons.length >= MAX_GRID_RESULTS && (
                  <p className="text-[10px] text-gray-400 text-center mt-2">
                    Showing first {MAX_GRID_RESULTS} results — type to narrow down
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default IconPicker;
